import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, List, ListItem, ListItemButton, ListItemText, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { DashboardListItem } from '../../../interfaces/IDashboard'
import { usePolling } from '../../../hooks/usePolling'
import { bucketColor } from './BucketColors'
import InfoTooltip from './InfoTooltip'
import CheckOutDeviationDialog from './CheckOutDeviationDialog'

interface PlannedDeviationsCardProps {
  variant?: 'compact' | 'wall'
  info?: string
}

function PlannedDeviationsCard({ variant = 'compact', info }: PlannedDeviationsCardProps) {
  const navigate = useNavigate()
  const isWall = variant === 'wall'
  const { data, refetch } = usePolling<DashboardListItem[]>('/api/stats/dashboard/drilldown?kind=planned-deviations', 5 * 60_000)
  const [checkOutTargetId, setCheckOutTargetId] = useState<string | null>(null)

  const items = data ?? []

  let body
  if (!data) {
    body = (
      <List dense disablePadding>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItem key={i} sx={{ borderLeft: '4px solid', borderColor: 'divider', mb: 0, py: 0.5 }}>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
              secondary={<Skeleton variant="text" width="40%" />}
            />
          </ListItem>
        ))}
      </List>
    )
  } else if (items.length === 0) {
    body = <Typography variant="body2" color="text.secondary">Ingen registrerte avvik</Typography>
  } else {
    body = (
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <List dense disablePadding>
          {items.map((item) => {
            const criticality = item.secondaryText.split(' · ')[0] ?? ''
            const border = bucketColor(criticality)
            return (
              <ListItem
                key={item.id}
                disablePadding
                sx={{ borderLeft: `4px solid ${border}`, mb: 0 }}
              >
                <ListItemButton onClick={() => navigate(item.linkPath)} sx={{ flex: 1, pr: 1, py: 0.25 }}>
                  <ListItemText
                    sx={{ my: 0 }}
                    primary={
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {item.id}
                        </Typography>
                        <Typography variant="body2" component="span" noWrap>
                          {item.primaryText}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.secondaryText}
                        </Typography>
                        {item.tertiaryText && (
                          <Typography component="span" variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                            {item.tertiaryText}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
                {!isWall && (
                  <Tooltip title="Sjekk ut avvik">
                    <Button
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={(e) => {
                        e.stopPropagation()
                        setCheckOutTargetId(item.id)
                      }}
                      sx={{ mr: 1, alignSelf: 'center', whiteSpace: 'nowrap' }}
                    >
                      Sjekk ut
                    </Button>
                  </Tooltip>
                )}
              </ListItem>
            )
          })}
        </List>
      </Box>
    )
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: isWall ? { xs: 2, md: 3 } : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isWall ? '45vh' : 480,
        minHeight: 0,
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography
            variant={isWall ? undefined : 'h6'}
            sx={{ fontSize: isWall ? 'clamp(1.25rem, 2vw, 2.25rem)' : undefined, fontWeight: 600 }}
          >
            Avvik på planlagt arbeid
          </Typography>
          {info && !isWall && <InfoTooltip text={info} />}
        </Stack>
        <Typography
          sx={{ fontSize: isWall ? 'clamp(1.5rem, 2.5vw, 2.75rem)' : undefined, fontWeight: 700 }}
          variant={isWall ? undefined : 'h6'}
          color={items.length > 0 ? undefined : 'text.secondary'}
        >
          {data ? items.length : '—'}
        </Typography>
      </Stack>
      {body}
      <CheckOutDeviationDialog
        open={checkOutTargetId !== null}
        eventId={checkOutTargetId}
        onClose={() => setCheckOutTargetId(null)}
        onCheckedOut={() => {
          refetch()
        }}
      />
    </Paper>
  )
}

export default PlannedDeviationsCard
